// TESSERAX — VALEUR DE SCORE (cœur pur, testé).
//
// Le calcul de score vivait inline dans main.ts (`@ts-nocheck`, non testé), ce qui
// avait laissé dériver l'équilibrage : combo indépendant de la DENSITÉ du clear,
// bonus CROIX (skill propre à la 3D) MOINS payé qu'un multi-clear générique, bonus
// de chute figés (+1/+2) devenus dérisoires en profondeur. On isole donc ici la
// VALEUR (pure, déterministe), et main.ts garde l'application des multiplicateurs de
// run (chaîne, runScoreMult, nextClearMult, Zone) et tout le juice.
//
// Module PUR : ni THREE ni DOM, pas de Math.random.
import type { ClearEval } from './grid';
import { isCrossClear } from './grid';

// Bonus d'étage plein (slab), par étage, ×level appliqué par l'appelant. Table
// historique conservée à l'identique (plafonnée à 5 étages dans une même résolution).
export const SLAB_TABLE = [0, 250, 600, 1100, 1700, 2400];
export function slabBonus(slabs: number): number {
  // Garde NaN / décimal / négatif : un index hors-borne (NaN, 2.7, −1) renverrait
  // SLAB_TABLE[?] = undefined → NaN propagé dans tout le score. On normalise en entier ≥0.
  const s = Number.isFinite(slabs) ? Math.max(0, Math.floor(slabs)) : 0;
  if (s <= 0) return 0;
  return SLAB_TABLE[Math.min(s, SLAB_TABLE.length - 1)];
}

// Bonus CROIX RELEVÉ : effacer les DEUX axes horizontaux d'un coup est le feat propre
// au puits 3D (impossible en 2D) ; il doit primer sur un multi-clear générique (le
// crochet Bascule paie 120·level). Coefficient relevé de 200 → CROSS_COEF.
export const CROSS_COEF = 300;
export function crossBonus(ev: ClearEval, level: number): number {
  if (!isCrossClear(ev)) return 0;
  return CROSS_COEF * level * Math.min(ev.xRows, ev.zRows);
}

// Valeur de base d'un clear AVANT multiplicateurs de chaîne / run / Zone (appliqués
// par main.ts). Reproduit le calcul historique (rowCount·30 + table d'étages, ×level)
// et y ajoute le bonus CROIX relevé. `isCross` sert au juice (bandeau « ✚ CROIX »).
export interface ClearScore { base: number; cross: number; isCross: boolean; }
export function scoreClear(ev: ClearEval, level: number): ClearScore {
  const rowsAndSlabs = ev.rowCount * 30 * level + slabBonus(ev.fullLayers.length) * level;
  const cross = crossBonus(ev, level);
  return { base: rowsAndSlabs + cross, cross, isCross: cross > 0 };
}

// Bonus TESSERA — le feat de la 4ᵉ dimension (« Le Pli »). Quand un Pli-fusion REPLIE le cube de
// préparation sur l'accumulateur et que la combinaison des deux tranches COMPLÈTE une ou plusieurs
// lignes, ces lignes sont des Tesseras : un effacement impossible dans un cube unique, l'apogée
// AU-DESSUS de la Croix (impossible en 2D). Il doit donc primer sur la Croix (300·level) : coef
// relevé à TESSERA_COEF, proportionnel aux lignes repliées. `rows` = lignes effacées PAR le Pli
// (1er palier de résolution post-fusion). PUR / déterministe : score uniquement, aucun RNG touché.
export const TESSERA_COEF = 600;
export function tesseraBonus(rows: number, level: number): number {
  if (!Number.isFinite(rows) || rows <= 0) return 0;
  return TESSERA_COEF * level * Math.floor(rows);
}

// Bonus d'EFFACEMENT PAR VRILLE (« spin clear ») — récompense le TUCK par rotation : la
// pièce n'a trouvé sa place finale QUE par une rotation avec recalage (wall-kick), là où
// aucune chute simple ne l'aurait menée, PUIS a effacé. C'est l'expression de skill la plus
// pure du genre (le « T-spin » du stacker), qui n'était récompensée par RIEN. Bonus
// proportionnel aux lignes effacées (≈ un petit combo : 100·level par ligne) — net et
// gratifiant, mais sans éclipser le placement ni la CROIX (300·level, le feat 3D). PUR /
// déterministe : score UNIQUEMENT, n'altère aucun tirage RNG → déterminisme du défi intact.
export const SPIN_COEF = 100;
export function spinClearBonus(rows: number, level: number): number {
  if (!Number.isFinite(rows) || rows <= 0) return 0;
  return SPIN_COEF * level * Math.floor(rows);
}

// Critère de VRILLE d'un recalage (wall-kick) — SOURCE UNIQUE main.ts ↔ core/sim (parité
// anti-triche). L'historique comptait TOUT kick (`spinTuck = i > 0`) : un simple pas de côté
// d'UNE case contre un mur (kicks 1..4) suffisait — tourner contre la paroi juste avant le
// verrouillage FERMAIT le bonus de vrille sans aucun « vissage » réel. Une vraie vrille
// exige un recalage SUBSTANTIEL : composante VERTICALE (la pièce se visse sous un surplomb /
// redescend d'un créneau) OU déport latéral ≥ 2 cases (diagonales incluses). Pur, tolérant.
export function isSpinKick(dx: number, dy: number, dz: number): boolean {
  const x = Number.isFinite(dx) ? Math.abs(dx) : 0;
  const y = Number.isFinite(dy) ? Math.abs(dy) : 0;
  const z = Number.isFinite(dz) ? Math.abs(dz) : 0;
  return y > 0 || x + z >= 2;
}

// CRESCENDO DE ZONE (mode 'bank') — LINÉAIRE + PALIER, plus d'exposant superlinéaire.
// L'historique `60·level·bank^1.55` faisait de la Zone maximale une stratégie STRICTEMENT
// dominante (2× de banque ⇒ ~2,9× de butin) et, pire, donnait à un journal FORGÉ (zone
// éternelle, cf. core/verify) un butin sans borne (~137M « vérifiés » pour 1000 lignes).
// La nouvelle courbe : `level·(COEF·bank + palier(bank))`, avec des paliers plats CALÉS
// sur les seuils nommés (core/zone.ZONE_TIERS) et CALIBRÉS pour reproduire à ~1 % près la
// valeur historique de CHAQUE palier nommé (TÉTRADE 328→330, HEXACUBE 968→960, ENNÉACUBE
// 1809→1790, DÉCACUBE 2817→2820, HYPERCUBE 5288→5280, OMNICUBE 10537→10480) → AUCUNE
// nerf ressentie dans la plage humaine ; seule la QUEUE au-delà d'OMNICUBE devient linéaire
// (l'abus s'effondre, le jeu réel ne bouge pas). SOURCE UNIQUE partagée main.ts ↔ core/sim
// (fidélité bit-exacte de l'anti-triche) — remplace deux copies synchronisées par commentaire.
// RÉVERSIBLE : ajuster ZONE_BANK_COEF / ZONE_TIER_FLAT.
export const ZONE_BANK_COEF = 110;
export const ZONE_TIER_FLAT: ReadonlyArray<[number, number]> = [
  [28, 7400], [18, 3300], [12, 1500], [9, 800], [6, 300], [3, 0],
];
export function zoneBankBonus(bankLines: number, level: number): number {
  const n = Number.isFinite(bankLines) ? Math.max(0, Math.floor(bankLines)) : 0;
  const l = Number.isFinite(level) && level > 0 ? Math.floor(level) : 1;
  if (n <= 0) return 0;
  let flat = 0;
  for (let i = 0; i < ZONE_TIER_FLAT.length; i++) if (n >= ZONE_TIER_FLAT[i][0]) { flat = ZONE_TIER_FLAT[i][1]; break; }
  return l * (ZONE_BANK_COEF * n + flat);
}

// Combo PONDÉRÉ PAR LA DENSITÉ (pas seulement la fréquence). Au même palier de série,
// une chaîne dense (multi-lignes / étages) paie plus qu'une chaîne fine. À rows=1,
// slabs=0 → facteur 1 → valeur historique exacte (streak·45·level·mult) : aucune
// régression pour un clear simple, seul le clear dense est davantage récompensé.
export const COMBO_BASE = 45;
export function comboBonus(streak: number, level: number, rows: number, slabs: number, mult = 1): number {
  if (streak < 2) return 0;
  const magnitude = 1 + 0.18 * Math.max(0, rows - 1) + 0.6 * Math.max(0, slabs);
  return Math.round(streak * COMBO_BASE * level * magnitude * mult);
}

// Bonus de chute, légèrement mis à l'échelle par la Profondeur pour rester pertinent
// en fin de partie (où un clear vaut 600+), sans jamais éclipser le placement. À
// level 1 : +1 (soft) / +2 par case (hard) → valeurs historiques exactes.
export function softDropBonus(level: number): number {
  return 1 + Math.floor(level / 6);
}
export function hardDropBonus(cells: number, level: number): number {
  if (cells <= 0) return 0;
  return cells * (2 + Math.floor(level / 5));
}

// CONTINUUM (« back-to-back ») — récompense un ENCHAÎNEMENT ININTERROMPU de clears de
// MAÎTRISE, axe distinct du COMBO (cascade interne à une résolution) et de la SÉRIE
// (clears sur pièces consécutives, mêmes triviaux). Un clear « de maîtrise » est un feat
// que le débutant ne produit pas par hasard : étage plein, Croix (deux axes), cascade
// (chaîne ≥2) ou triple ligne+. C'est l'expression de skill qui mérite un palier de
// récompense propre, à la manière du back-to-back des grands jeux de blocs.
export interface HardClearShape { rows: number; slabs: number; cross: number; chain: number; }
export function isHardClear(res: HardClearShape): boolean {
  return res.slabs >= 1 || res.cross >= 1 || res.chain >= 2 || res.rows >= 3;
}

// Bonus de CONTINUUM, versé À PARTIR du 2ᵉ clear de maîtrise consécutif. `prior` = nombre
// de clears de maîtrise DÉJÀ enchaînés avant celui-ci (≥1 ⇒ ce clear prolonge le continuum).
// Le facteur croît par palier puis PLAFONNE (+50 % → +150 %) : il récompense la constance
// sans jamais éclipser la valeur de placement ni exploser le score (cf. garde-fou
// d'équilibrage). Proportionnel au butin du clear (`gained`, déjà multiplié chaîne/run/Zone)
// → un continuum de gros clears paie plus qu'un continuum de clears fins. PUR/déterministe :
// score uniquement, n'altère aucun tirage RNG → déterminisme du défi du jour intact.
export const B2B_STEP = 0.25;
export const B2B_CAP = 1.5;
export function continuumBonus(prior: number, gained: number): number {
  if (prior < 1 || gained <= 0) return 0;
  const factor = Math.min(0.5 + B2B_STEP * (prior - 1), B2B_CAP);
  return Math.round(gained * factor);
}

// ===== UNIF-CONST-001 — LOI PARTAGÉE sim↔vif ==========================================
// Ces coefficients étaient DUPLIQUÉS en littéraux « synchronisés par commentaire » dans les
// DEUX moteurs (core/sim.ts et game/rules.ts, contrat bit-exact de l'anti-triche) : toute
// édition unilatérale = rejet en masse des scores légitimes soumis avec journal. On les
// hoiste ici, en SOURCE UNIQUE consommée par les deux côtés — mêmes expressions, même ordre
// d'opérations flottantes (bit-exactitude préservée, verrouillée par les goldens et le test
// de fidélité sim↔vif de la CI). PUR : arithmétique seule.

// Multiplicateur de CHAÎNE (cascade) : ×1, ×1.6, ×2.2, … (+CHAIN_MULT_STEP par maillon).
export const CHAIN_MULT_STEP = 0.6;
export function chainMult(chain: number): number { return 1 + (chain - 1) * CHAIN_MULT_STEP; }

// Zone CLASSIQUE (A/B `?zone=classic`) : multiplicateur croissant par clear en Zone, et
// bonus de sortie par clear engrangé (×level, appliqué par l'appelant).
export const ZONE_CLASSIC_BASE = 3;
export const ZONE_CLASSIC_STEP = 0.6;
export function zoneClassicMult(zoneClears: number): number { return ZONE_CLASSIC_BASE + zoneClears * ZONE_CLASSIC_STEP; }
export const ZONE_CLASSIC_EXIT_COEF = 300;

// PERFECT (puits entièrement vidé) : gros bonus plat ×level.
export const PERFECT_COEF = 2500;
export function perfectBonus(level: number): number { return PERFECT_COEF * level; }

// Économie de JAUGE (Zone) par résolution : par rangée, par étage, plus un forfait par verrou.
export const METER_PER_ROW = 9;
export const METER_PER_SLAB = 30;
export const METER_PER_LOCK = 2;
export function meterGain(rows: number, slabs: number): number { return rows * METER_PER_ROW + slabs * METER_PER_SLAB + METER_PER_LOCK; }

// Bonus de BASCULE (hors écho Miroir), AVANT runScoreMult (le Math.round(×runScoreMult)
// reste chez l'appelant, à l'identique des deux moteurs). Ordre d'accumulation FIGÉ :
// multi → cascade → réparation → sauvetage (l'ordre des additions flottantes fait partie
// du contrat bit-exact).
export const BASC_MULTI_COEF = 120;
export const BASC_CASCADE_COEF = 200;
export const BASC_REPAIR_COEF = 40;
export const BASC_SAVE_COEF = 300;
export interface BasculeFeats { multi: boolean; cascade: boolean; chain: number; repairedHoles: number; save: boolean; }
export function basculeBonus(level: number, f: BasculeFeats): number {
  let b = 0;
  if (f.multi) b += BASC_MULTI_COEF * level;
  if (f.cascade) b += BASC_CASCADE_COEF * level * (f.chain - 1);
  if (f.repairedHoles > 0) b += f.repairedHoles * BASC_REPAIR_COEF * level;
  if (f.save) b += BASC_SAVE_COEF * level;
  return b;
}
